import React, { useState, useEffect } from 'react';
import './CookieConsent.css';
import { initGA } from './GoogleAnalytics';

// CONFIGURATION
const CONFIG = {
  enabled: true,
  position: 'bottom', // 'bottom', 'top', 'bottom-left', 'bottom-right'
  style: 'bar', // 'bar', 'modal', 'compact'
  showPreferences: true, // Show detailed cookie preferences
  cookieName: 'ubotika_cookie_consent',
  expiryDays: 365,
  message: 'We use cookies to analyze traffic and improve your experience.',
  privacyPolicyUrl: '/privacy',
  categories: {
    necessary: {
      enabled: true,
      readonly: true, // Cannot be disabled
      label: 'Necessary',
      description: 'Essential cookies for website functionality',
    },
    analytics: {
      enabled: true,
      readonly: false,
      label: 'Analytics',
      description: 'Help us understand how visitors interact with our website',
    },
    marketing: {
      enabled: false,
      readonly: false,
      label: 'Marketing',
      description: 'Used to deliver personalized advertisements',
    },
  },
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState(CONFIG.categories);
  const [taraWidgetActive, setTaraWidgetActive] = useState(false);

  useEffect(() => {
    if (!CONFIG.enabled) return;

    // Check if user has already given consent
    const consent = localStorage.getItem(CONFIG.cookieName);
    if (!consent) {
      // Delay showing banner slightly for better UX
      setTimeout(() => setVisible(true), 1000);
    }
  }, []);

  // Listen for Tara widget events to hide cookie consent when active
  useEffect(() => {
    const handleTaraWidget = (e) => {
      setTaraWidgetActive(e.detail.active);
    };

    window.addEventListener('tara-widget-active', handleTaraWidget);
    return () => {
      window.removeEventListener('tara-widget-active', handleTaraWidget);
    };
  }, []);

  const saveConsent = (consentData) => {
    const consentObject = {
      timestamp: new Date().toISOString(),
      preferences: consentData,
    };
    localStorage.setItem(CONFIG.cookieName, JSON.stringify(consentObject));
    setVisible(false);
    setShowPreferences(false);

    // Initialize analytics or other scripts based on consent
    if (consentData.analytics?.enabled) {
      // Initialize Google Analytics
      initGA();
    }
    if (consentData.marketing?.enabled) {
      // Enable marketing cookies
      // Add marketing script initialization here if needed
    }
  };

  const handleAcceptAll = () => {
    const allEnabled = {};
    Object.keys(CONFIG.categories).forEach(key => {
      allEnabled[key] = { enabled: true };
    });
    saveConsent(allEnabled);
  };

  const handleRejectAll = () => {
    const necessaryOnly = {};
    Object.keys(CONFIG.categories).forEach(key => {
      necessaryOnly[key] = {
        enabled: CONFIG.categories[key].readonly ? true : false,
      };
    });
    saveConsent(necessaryOnly);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const handleToggleCategory = (category) => {
    if (CONFIG.categories[category].readonly) return;

    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        enabled: !prev[category].enabled,
      },
    }));
  };

  if (!CONFIG.enabled || !visible || taraWidgetActive) return null;

  return (
    <>
      <div className={`cookie-consent-overlay ${showPreferences ? 'visible' : ''}`} />
      <div className={`cookie-consent ${CONFIG.position} ${CONFIG.style} ${visible && !taraWidgetActive ? 'visible' : ''}`}>
        {!showPreferences ? (
          // Main consent banner
          <div className="cookie-consent-content">
            <div className="cookie-consent-text">
              <p>{CONFIG.message}</p>
              {CONFIG.privacyPolicyUrl && (
                <a
                  href={CONFIG.privacyPolicyUrl}
                  className="cookie-consent-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              )}
            </div>
            <div className="cookie-consent-actions">
              {CONFIG.showPreferences && (
                <button
                  className="cookie-consent-btn secondary"
                  onClick={() => setShowPreferences(true)}
                >
                  Preferences
                </button>
              )}
              <button
                className="cookie-consent-btn secondary"
                onClick={handleRejectAll}
              >
                Reject All
              </button>
              <button
                className="cookie-consent-btn primary"
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          // Preferences panel
          <div className="cookie-preferences">
            <div className="cookie-preferences-header">
              <h3>Cookie Preferences</h3>
              <button
                className="cookie-preferences-close"
                onClick={() => setShowPreferences(false)}
                aria-label="Close preferences"
              >
                ✕
              </button>
            </div>
            <div className="cookie-preferences-body">
              <p className="cookie-preferences-intro">
                We use different types of cookies to optimize your experience on our website.
                Click on the categories below to learn more and customize your preferences.
              </p>
              <div className="cookie-categories">
                {Object.entries(CONFIG.categories).map(([key, category]) => (
                  <div key={key} className="cookie-category">
                    <div className="cookie-category-header">
                      <div className="cookie-category-info">
                        <h4>{category.label}</h4>
                        <p>{category.description}</p>
                      </div>
                      <label className="cookie-toggle">
                        <input
                          type="checkbox"
                          checked={preferences[key]?.enabled ?? category.enabled}
                          onChange={() => handleToggleCategory(key)}
                          disabled={category.readonly}
                        />
                        <span className="cookie-toggle-slider"></span>
                      </label>
                    </div>
                    {category.readonly && (
                      <span className="cookie-category-badge">Always Active</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="cookie-preferences-footer">
              <button
                className="cookie-consent-btn secondary"
                onClick={() => setShowPreferences(false)}
              >
                Cancel
              </button>
              <button
                className="cookie-consent-btn primary"
                onClick={handleSavePreferences}
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CookieConsent;
